import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, Lock, CheckCircle2, Clock, Award, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LearnPart() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const token = localStorage.getItem('guestToken') || '';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      const url = token ? `${API}/elearning/part/${slug}?token=${token}` : `${API}/elearning/part/${slug}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) setData(json);
      else toast.error(json.detail || 'Could not load lessons');
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  const openLesson = async (lesson) => {
    if (!lesson.free_preview && !token) {
      toast.error('Please sign in to watch this lesson');
      navigate('/portal');
      return;
    }
    try {
      const url = token
        ? `${API}/elearning/lesson/${lesson.id}/play?token=${token}`
        : `${API}/elearning/lesson/${lesson.id}/play`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setActiveLesson({ ...lesson, ...json.lesson });
        setIframeUrl(json.iframe_url);
      } else {
        toast.error(json.detail || 'Could not load lesson');
      }
    } catch (e) { toast.error('Could not load lesson'); }
  };

  const markComplete = async () => {
    if (!activeLesson) return;
    try {
      const res = await fetch(`${API}/elearning/lesson/${activeLesson.id}/progress?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true, played_fraction: 1, played_seconds: activeLesson.duration_sec || 0 })
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Lesson marked complete!');
        if (json.certificate) {
          toast.success('🎉 You earned a certificate for this part!', { duration: 6000 });
        }
        setActiveLesson(null);
        setIframeUrl('');
        load();
      }
    } catch (e) { toast.error('Could not save progress'); }
  };

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Part not found</div>;

  const { part, lessons, completion_pct, unlocked, lock_reason } = data;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-md border-b border-amber-500/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <button onClick={() => navigate('/learn')} className="flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm" data-testid="learnpart-back">
            <ArrowLeft className="w-4 h-4" /> Program
          </button>
          <div className="text-center">
            <p className="text-[10px] text-amber-300 uppercase tracking-[3px]">Part {part.order}</p>
            <h1 className="text-sm sm:text-base font-bold">{part.title}</h1>
          </div>
          <div className="w-20 text-right text-xs">
            <span className="font-mono text-amber-300">{completion_pct.toFixed(0)}%</span>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-20">
        {!unlocked && lessons.some(l => l.free_preview) && (
          <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/30 text-center mb-6" data-testid="learn-preview-banner">
            <p className="text-sm text-emerald-200">
              <span className="font-bold">🎬 Free preview mode.</span> You can watch the lessons marked "Free preview" below. Unlock this part to access all lessons & earn the certificate.
            </p>
          </div>
        )}
        {!unlocked && !lessons.some(l => l.free_preview) && (
          <div className="rounded-xl p-5 bg-zinc-950 border border-amber-500/30 text-center mb-6">
            <Lock className="w-8 h-8 mx-auto mb-3 text-amber-300" />
            <p className="text-sm text-gray-300">
              {lock_reason === 'complete_previous' && 'Complete the previous part before accessing this one.'}
              {lock_reason === 'buy_to_unlock' && 'Purchase this part to unlock all lessons.'}
              {lock_reason === 'sign_in_required' && 'Sign in to access lessons.'}
            </p>
            <Button onClick={() => navigate('/learn')} className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-bold">
              Back to program
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {lessons.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-12">
              Lessons coming soon. Admin is preparing the content.
            </p>
          )}
          {lessons.map((lesson, idx) => {
            const canPlay = (unlocked || lesson.free_preview) && lesson.has_video && lesson.video_status !== 'processing';
            return (
              <Card key={lesson.id} className={`bg-zinc-950/80 border ${lesson.progress.completed ? 'border-emerald-500/40' : 'border-zinc-800'} hover:border-amber-500/30 transition-colors`} data-testid={`lesson-card-${idx}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-300 font-bold shrink-0">
                    {lesson.progress.completed ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : (idx + 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-sm sm:text-base">{lesson.title}</h3>
                      {lesson.free_preview && (
                        <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px]">Free preview</Badge>
                      )}
                      {lesson.video_status === 'processing' && (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[9px]">
                          <Clock className="w-2.5 h-2.5 mr-1" /> Processing
                        </Badge>
                      )}
                    </div>
                    {lesson.description && (
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{lesson.description}</p>
                    )}
                    {lesson.duration_sec && (
                      <p className="text-[10px] text-gray-500 mt-1 font-mono">
                        {Math.floor(lesson.duration_sec / 60)}:{String(lesson.duration_sec % 60).padStart(2, '0')}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => openLesson(lesson)}
                    disabled={!canPlay}
                    className={canPlay ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-zinc-800 text-gray-500 cursor-not-allowed'}
                    data-testid={`lesson-play-${idx}`}
                  >
                    {!lesson.has_video ? 'No video' :
                      lesson.video_status === 'processing' ? 'Processing' :
                        canPlay ? <><Play className="w-3.5 h-3.5 mr-1" /> Watch</> : <Lock className="w-3.5 h-3.5" />}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {completion_pct >= 100 && (
          <div className="mt-8 rounded-2xl p-6 text-center bg-gradient-to-br from-amber-500/15 to-emerald-500/10 border-2 border-amber-400/40">
            <Award className="w-12 h-12 mx-auto text-amber-300 mb-2" />
            <h3 className="text-xl font-bold">Part {part.order} Complete!</h3>
            <p className="text-sm text-gray-300 mt-1">Your certificate is ready. Head back to the program page to download it.</p>
            <Button onClick={() => navigate('/learn')} className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="part-complete-back">
              Back to program
            </Button>
          </div>
        )}
      </section>

      {/* Lesson player modal */}
      {activeLesson && iframeUrl && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => { setActiveLesson(null); setIframeUrl(''); }}>
          <div className="w-full max-w-5xl bg-zinc-950 rounded-2xl overflow-hidden border-2 border-amber-500/30" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <div className="min-w-0">
                <h3 className="font-bold text-sm sm:text-base truncate">{activeLesson.title}</h3>
                <p className="text-[10px] text-gray-500">{part.title}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={markComplete} className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs" data-testid="lesson-mark-complete">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark complete
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setActiveLesson(null); setIframeUrl(''); }} className="border-zinc-700 text-xs" data-testid="lesson-close">
                  Close
                </Button>
              </div>
            </div>
            <div className="aspect-video bg-black w-full">
              <iframe
                src={iframeUrl}
                title={activeLesson.title}
                loading="lazy"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="lesson-player-iframe"
              />
            </div>
            {activeLesson.description && (
              <div className="p-4 text-sm text-gray-300 border-t border-zinc-800">
                <div className="flex items-center gap-2 text-xs text-amber-300 mb-2 uppercase tracking-wider">
                  <ImageIcon className="w-3 h-3" /> Notes
                </div>
                <p className="whitespace-pre-wrap">{activeLesson.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
